import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Training & Coaching";
const heroImage = "https://net-basis.net/Bilder/ict-training-coaching-dach-market-entry.webp";
const heroAlt = "Businessman visualizes sales, leadership and intercultural competence goals in the DACH market.";
const heroClaimLine1 = "Guided to\nyour goal!";
const benefitsTitle = "How you benefit from our __Training & Coaching__ expertise:";
const p1Text = "Whether it is the general definition of strategy and structures to drive and lead such a business, or the focus on clearly defined project descriptions with agreed milestones, cost calculations, and \"Return on Investment\", or everything related to sales and marketing activities – a market entry project comes with many essential and fundamental topics that require proper care and handling if you want this plan to be successful.";
const p2Text = "It becomes even more challenging when everything needs to be done for a foreign market, in a different language, in a different environment, and in a different culture.";
const p3Text = "If you, as a manufacturer, nevertheless decide to venture into and execute the market entry independently and with your own staff, NetBasis International can still support you and accompany you as a whole or in specific areas with individual training or coaching. In doing so, we deliberately focus on the transfer of knowledge and skills by working alongside individual employees or entire teams of your company in line with your objective. And, our claim to ourselves is to act as flexibly as required, to deliver outstanding quality, and to become your trusted advisor over time.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Preparatory meetings for the precise definition of training and coaching objectives",
  "Definition of a clear vision to safely guide decision-making and actions to be taken",
  "Holistic consideration of all essential areas for your sustainable business development",
  "Sound situation assessment as well as structured problem and potential analysis",
  "Strategic guidance for the clear development of your market entry strategy",
  "Creation of an individual action plan with measurable project management goals",
  "Market-related analysis of products, solutions, and the relevant competition",
  "Best possible product presentation and effective self-presentation before decision-makers",
  "Establishing the right model for building relationships with potential customers for successful business deals",
  "Market-driven customer acquisition, excellent customer service, and long-term customer retention",
  "Targeted support for your employees to make their project work demonstrably effective",
  "Sustainable top-team development and effective group and team leadership",
  "Provision of permanent strategic support for CEO, board, and management",
  "Execution of tailored coaching sessions with clear follow-up measures",
];

export default function TrainingCoachingEnPage() {
  const strongName = COMPANY_NAME;
  const p1Parts = p1Text.split(strongName);
  const p2Parts = p2Text.split(strongName);
  const p3Parts = p3Text.split(strongName);

  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Training & Coaching | NetBasis International"
        description="Practice-oriented training and coaching for sales teams for the successful implementation of international B2B strategies."
        ogUrl="https://net-basis.net/en/services/training-coaching"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start pt-[8%] md:pt-[calc(9%-1.5cm)] lg:pt-[calc(8%-1.5cm)] pl-6 md:pl-[calc(1.5rem+1cm)] pr-4 pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-left training-text-slide"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                    lineHeight: '1.5',
                  }}
                >
                  {heroClaimLine1.split('\n').map((line, i) => (
                    <span key={i} className="block">{line}</span>
                  ))}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="text-foreground-950 leading-relaxed text-sm md:text-base font-semibold mb-4">
              Your trusted partner for individual training and strategic coaching in the DACH region. We accompany foreign manufacturers during their independent market entry and secure the success of your local teams through targeted knowledge transfer.
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p3Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p3Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>

            </div>

            {/* Benefits heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>

            {/* Widget 6 */}
            <p className="font-heading text-sm md:text-base text-primary-600 font-bold mt-8 md:mt-10 mb-3 md:mb-4">
              Why training courses, seminars, and business coaching?
            </p>

            {/* Widget 7 */}
            <p className="text-foreground-950 leading-relaxed text-sm md:text-base">
              Your vision of opening up one or more new markets receives a perfectly adapted structure and an actionable approach. This ensures harmony with your entire corporate culture and guarantees that your international project becomes a sustainable success.
            </p>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}